<template>
	<div
		class="kk-tabs"
		:class="{
			'kk-tabs--has-content': $slots['default'],
			'kk-tabs--show-icon': tabs[activeTabIndex] && tabs[activeTabIndex].iconName,
		}"
	>
		<div
			ref="header"
			class="kk-tabs__header"
		>
			<div class="kk-tabs__header-padding-left"></div>
			<nav class="kk-tabs__tab-list">
				<a
					v-for="(tab, index) in tabs"
					ref="tab"
					:key="tab.name"
					:class="{
						'kk-tabs__tab--active': activeTabIndex === index,
					}"
					class="kk-tabs__tab"
					role="button"
					href="#"
					@click.prevent="updateActiveTab(tab)"
				>
					<KkIcon
						v-if="tab.iconName"
						class="kk-tabs__tab-icon"
						:name="tab.iconName"
						:size="'sm'"
					/>
					<div
						class="kk-tabs__tab-text"
						:class="{
							'kk-tabs__tab-text--badge-at-corner': tab.isShowBadge && !tab.badgeValue,
						}"
					>
						<span>
							{{ tab.text }}
						</span>
						<KkBadge
							v-if="tab.isShowBadge"
							class="kk-tabs__tab-badge"
							:value="tab.badgeValue"
							:dot-size="'md'"
						/>
					</div>
				</a>
			</nav>
			<div class="kk-tabs__header-padding-right"></div>
			<div
				ref="indicator"
				class="kk-tabs__indicator"
				:style="indicatorStyle"
			></div>
		</div>
		<slot />
	</div>
</template>

<script>
import KkBadge from '../../KkBadge';
import KkIcon from '../../KkIcon';
import kkScrollTo from '../../../libs/kkScrollTo';
import {
	kkCheckIsMouseDraggingToScroll,
	kkSetMouseDragToScroll,
	kkUnsetMouseDragToScroll,
} from '../../../libs/kkMouseDragToScroll';

const unitMultiTimesMap = new Map([
	['ms', 1],
	['s', 1000],
]);

/**
 * 將 css duration 的值轉成 milliseconds 的數字
 *
 * @param {String} durationWithUnit - value of duration in css（單位為 ms/s）
 * @return {Number} - 去掉單位的 milliseconds 數字（無法正確轉換回傳 0）
 *
 * @example
 * getDurationNumber('250ms') -> 250
 * getDurationNumber('.25s') -> 250
 */
const getDurationNumber = durationWithUnit => {
	for (const [unit, multiTimes] of unitMultiTimesMap) {
		if (!durationWithUnit?.endsWith(unit)) {
			continue;
		}
		const millisecond = durationWithUnit?.replace(unit, '') * multiTimes;
		if (isNaN(millisecond)) {
			console.error('duration is NaN');
			break;
		}
		return millisecond;
	}
	return 0;
}

export default {
	name: 'KkTabs',
	components: {
		KkBadge,
		KkIcon,
	},
	props: {
		tabs: {
			type: Array,
			default: () => [],
		},
		activeTabName: {
			type: [String, null],
			default: null,
		},
	},
	data() {
		return {
			indicatorStyle: null,
		};
	},
	computed: {
		activeTabIndex() {
			return this.tabs.findIndex(tab => tab.name === this.activeTabName);
		},
	},
	watch: {
		activeTabIndex() {
			this.$nextTick(this.updateIndicatorStyle);
			this.$nextTick(this.scrollHeaderToActiveTab);
		},
		tabs: {
			deep: true,
			handler() {
				this.$nextTick(this.updateIndicatorStyle);
			},
		},
		indicatorStyle: {
			immediate: true,
			handler(newVal, oldVal) {
				// temporarily turn off indicator's transition when it appears / disappears
				if (newVal && !oldVal || !newVal && oldVal) {
					this.$refs.indicator.style.transition = 'none';
					setTimeout(() => {
						this.$refs.indicator.style.transition = '';
					}, 0);
				}
			},
		},
	},
	created() {
		if (this.activeTabIndex === -1 && this.tabs.length) {
			this.updateActiveTab(this.tabs[0]);
		}
	},
	mounted() {
		this.updateIndicatorStyle();
		this.scrollHeaderToActiveTab();
		kkSetMouseDragToScroll(this.$refs.header);
	},
	beforeDestroy() {
		kkUnsetMouseDragToScroll(this.$refs.header);
	},
	methods: {
		updateActiveTab(tab) {
			if (kkCheckIsMouseDraggingToScroll(this.$refs.header)) {
				// don't trigger update after user dragged header
				return;
			}
			this.$emit('update:active-tab-name', tab.name);
		},
		updateIndicatorStyle() {
			if (this.activeTabIndex === -1) {
				this.indicatorStyle = null;
				return;
			}
			const activeTabElement = this.$refs.tab[this.activeTabIndex];
			const computedStyle = window.getComputedStyle(this.$el);
			const iconOffsetWidth = parseInt(computedStyle.getPropertyValue('--tab-icon-offset-width'));
			const tabPaddingX = parseInt(computedStyle.getPropertyValue('--tab-padding-x'));
			this.indicatorStyle = {
				'--indicator-width': activeTabElement.offsetWidth - tabPaddingX * 2 + iconOffsetWidth + 'px',
				'--indicator-offset': activeTabElement.offsetLeft + tabPaddingX + 'px',
			}
		},
		scrollHeaderToActiveTab() {
			if (this.activeTabIndex === -1) {
				return;
			}
			const headerElement = this.$refs.header;
			const activeTabElement = this.$refs.tab[this.activeTabIndex];
			const iconOffsetWidth = parseInt(window.getComputedStyle(this.$el).getPropertyValue('--tab-icon-offset-width'));
			const target = (activeTabElement.offsetLeft + (activeTabElement.offsetWidth + iconOffsetWidth) / 2) - headerElement.clientWidth / 2;
			const transitionDuration = window.getComputedStyle(this.$el).getPropertyValue('--tab-transition-duration');

			kkScrollTo({
				scrollElement: headerElement,
				x: Math.min(Math.max(0, target), headerElement.scrollWidth - headerElement.clientWidth),
				duration: getDurationNumber(transitionDuration),
			});
		},
	},
};
</script>
